/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useState, useEffect, KeyboardEvent } from 'react';
import { RadioButton } from '@marriott/mi-ui-library-shop';
import { renderedData } from '../../organisms/SearchResults/searchResultsMainComponent';
import { METERSTOKM, MILESTOMETERS } from '../../organisms/SearchResults/lib/application.constants';
import {
  SearchFacetsResponse,
  SearchStateExtendsType,
  SearchStateFilterObj,
} from '../../organisms/SearchResults/store/store.type';
import {
  DefaultValue,
  SearchFacetsList,
  SearchFacetsListCodes,
  hardCodedAllLocale,
} from '../../organisms/SearchResults/index.constant';
import { SearchFilterContext } from '../../organisms/SearchResults/index.context';
import { StyledDistanceContainerDiv, StyledDistanceItemDiv, StyledDistanceItemsContainerDiv } from './index.styles';
import { useWindowSize } from '../../hooks';
import { formatNumberByLocale } from '../../utils';
import { useSessionUpdateContext } from '../../context/SessionUpdateContext';
import { usePageContext } from '../../context/PageContext/index';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

export const DistanceFilterComponent: React.FC<{
  onItemSelect: (arg: SearchFacetsList, arg2: SearchStateFilterObj) => void;
  clearFilterState: SearchStateExtendsType | undefined | null;
  distancesList?: SearchFacetsResponse;
}> = ({ clearFilterState, onItemSelect, distancesList }) => {
  const { currentLocale } = usePageContext();
  const DEFAULT_DISTANCE_VALUE = DefaultValue;

  const { searchQueryState } = useContext(SearchFilterContext);
  const { interactionsSelected, setInteractionsSelected } = useSessionUpdateContext();

  const { jsonContent }: any = useContext(renderedData);
  const clickTrackingLoc = 'Phoenix Search Results';

  const [currChecked, setCurrChecked] = useState<string | number>(DEFAULT_DISTANCE_VALUE);
  const { width } = useWindowSize();
  const distanceList: SearchFacetsResponse =
    distancesList ??
    searchQueryState?.filter?.filter(
      (key: SearchFacetsResponse) => key.type.code === SearchFacetsListCodes.distance
    )[0];

  const getDistanceLabelVal = (disc: any): string => {
    if (currentLocale !== hardCodedAllLocale.en) {
      return `${Math.round(disc?.start / METERSTOKM)} - ${Math.round(disc?.end / METERSTOKM)} ${
        jsonContent?.distanceMeasurementUnit == 'distanceMeasurementUnit' ? 'km' : jsonContent?.distanceMeasurementUnit
      }`;
    } else {
      return `${Math.round(disc?.start / MILESTOMETERS)} - ${Math.round(disc?.end / MILESTOMETERS)} ${
        jsonContent?.distanceMeasurementUnit == 'distanceMeasurementUnit'
          ? 'miles'
          : jsonContent?.distanceMeasurementUnit
      }`;
    }
  };

  const onDistanceRangeChange = (pr: number | string): void => {
    setCurrChecked(pr);
    if (pr === DEFAULT_DISTANCE_VALUE) {
      onItemSelect(SearchFacetsList.distance, {});
    } else {
      const str = typeof pr === 'number' ? String(pr) : pr;
      onItemSelect(SearchFacetsList.distance, { [str]: pr });
    }
    setInteractionsSelected({ ...interactionsSelected, distance: true });
  };

  const onSelectionChange = (event: any, code: string | number): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      onDistanceRangeChange(code);
    }
  };

  useEffect(() => {
    if (clearFilterState === undefined || clearFilterState === null) {
      setCurrChecked(DEFAULT_DISTANCE_VALUE);
    }
  }, [clearFilterState]);

  useEffect(() => {
    setCurrChecked(Object.values(searchQueryState[SearchFacetsList.distance] ?? {})[0] ?? DEFAULT_DISTANCE_VALUE);
  }, [searchQueryState[SearchFacetsList.distance]]);

  return distanceList?.type?.count ? (
    <StyledDistanceContainerDiv className="t-font-alt-m">
      <fieldset>
        <div className="t-font-alt-m">
          <legend>
            {' '}
            <h4
              className={
                (width && width > 1024 ? `t-subtitle-l` : width && width > 767 ? `t-subtitle-l` : `t-subtitle-m`) +
                ' filter-heading'
              }
            >
              {distanceList?.type?.label}
            </h4>
          </legend>
        </div>
        <StyledDistanceItemsContainerDiv className="hotel-price">
          <StyledDistanceItemDiv className="price-item any-price" key="any-distance-key-pr">
            <RadioButton
              radiobuttonName="distance_radio"
              checked={currChecked === 'default'}
              customClickClass="radio-input custom_click_track"
              radiobuttonId="any-distance-key-radio"
              onChange={(): void => onDistanceRangeChange('default')}
              className="m-radio-button-m radio-container"
              {...{
                custom_click_track_value: `${clickTrackingLoc}| ${distanceList?.type?.label} Selection: ${jsonContent.anyDistanceLabel} |internal`,
              }}
              labelClassName="t-font-s col-12 item-name checkbox-radio"
              radiobuttonLabel={`${jsonContent?.anyDistanceLabel}`}
              setTabIndex={0}
            ></RadioButton>
          </StyledDistanceItemDiv>
          {distanceList?.buckets?.map((distance: any, idx: number) => (
            <StyledDistanceItemDiv
              className={`price-item ${distance?.count === 0 ? 'disabled' : ''}`}
              key={`distance${distance?.index}pr${idx}`}
            >
              <RadioButton
                radiobuttonName="distance_radio"
                checked={currChecked === distance.index}
                disabled={distance?.count === 0}
                customClickClass="radio-input custom_click_track"
                radiobuttonId={`distance_radio${idx}`}
                onChange={(): void => onDistanceRangeChange(distance.index)}
                className="m-radio-button-m radio-container"
                {...{
                  custom_click_track_value: `${clickTrackingLoc}| Distance options: ${getDistanceLabelVal(
                    distance
                  )} |internal `,
                  onKeyDown: (e: KeyboardEvent): void => onSelectionChange(e, distance.index),
                }}
                labelClassName="t-font-s col-12 item-name checkbox-radio"
                radiobuttonLabel={`${getDistanceLabelVal(distance)}  ${
                  distance.count !== 0 ? `(${formatNumberByLocale(distance?.count, currentLocale)})` : ''
                }`}
                setTabIndex={distance?.count === 0 ? -1 : 0}
              ></RadioButton>
            </StyledDistanceItemDiv>
          ))}
        </StyledDistanceItemsContainerDiv>
      </fieldset>
      <hr aria-hidden className="filter-separator" />
    </StyledDistanceContainerDiv>
  ) : null;
};
